/**
 * FE mirror of the handheld naming helpers. Backend source of truth:
 * functions/src/types/handheld.ts (keep in sync — exact mockup behavior).
 * Pure functions, used by the handheld views to render machine names.
 */
export const PPT_CATEGORIES = ["Environment", "Object", "Person / Animal", "Vehicle", "Other"];

/**
 * The job's human label / name stem (e.g. walwal_260312). Legacy nyc-suite jobs
 * have a random Firestore doc id with the real id in the `jobId` FIELD; newer
 * jobs use the human id AS the doc id. Same convention as the rig view
 * (CaptureArraysView: `j.jobId || j.id`). The doc id stays the Firestore key —
 * this label is for display and derived folder/file names only (backend mirror:
 * readJobNameStem in functions/src/services/handheldAllocator.ts).
 */
export const jobNameLabel = (job) => job?.jobId || job?.displayName || job?.id || "";

/** 3 → "003". Per-job subject number, zero-padded (display label). */
export const pad3 = (n) => String(n ?? 0).padStart(3, "0");

/** 3 → "03". Per-job project serial, zero-padded. */
export const pad2 = (n) => String(n ?? 0).padStart(2, "0");

/** The stable machine stem: walwal_260514_leo_003. */
export const exportStem = (jobId, code, number) => `${jobId}_${code}_${pad3(number)}`;

/** Full filename = stem + optional suffix. */
export const exportFilename = (jobId, code, number, suffix) => {
  const stem = exportStem(jobId, code, number);
  return suffix ? `${stem}_${suffix}` : stem;
};

/**
 * The names of one capture attempt (Q37; Ivin, 2026-10-07: every attempt has
 * its own serial number, so its files are named with it). Backend:
 * attemptSerialForName / attemptStem / attemptExportFilename /
 * attemptRawFolderName in functions/src/types/handheld.ts (the shared vector
 * functions/src/__tests__/fixtures/handheld-naming-parity.json holds both to
 * the same answers). Attempt 1 keeps the names it had before attempts
 * existed, exactly: walwal_260514_leo_003_400k. A retake carries its own
 * serial number after the subject number:
 * walwal_260514_leo_003_walwal2605140012_400k. Null when a retake's serial
 * number is not recorded.
 */
export const attemptSerialForName = (attempts, attempt) => {
  const n = attempt ?? 1;
  if (n <= 1) return { sn: null };
  const sn = (Array.isArray(attempts) ? attempts : []).find((a) => a.n === n)?.sn;
  return typeof sn === "string" && sn ? { sn } : null;
};

export const attemptStem = (jobId, code, number, attempt, attempts) => {
  const serial = attemptSerialForName(attempts, attempt);
  if (!serial) return null;
  const stem = exportStem(jobId, code, number);
  return serial.sn ? `${stem}_${serial.sn}` : stem;
};

export const attemptExportFilename = (jobId, code, number, suffix, attempt, attempts) => {
  const stem = attemptStem(jobId, code, number, attempt, attempts);
  if (stem === null) return null;
  return suffix ? `${stem}_${suffix}` : stem;
};

export const attemptRawFolderName = (jobId, code, number, suffix, attempt, attempts) =>
  attemptExportFilename(jobId, code, number, suffix, attempt, attempts);

/** The attempt a new raw set or export goes to when none is named: the subject's latest (1 with none recorded; the backend's resolveAttempt). */
export const latestAttempt = (subject) => {
  const ns = (Array.isArray(subject?.attempts) ? subject.attempts : []).map((a) => a.n).filter(Number.isFinite);
  return ns.length ? Math.max(...ns) : 1;
};

/**
 * A name as Windows tells names apart (backend windowsNameKey): trailing dots
 * and spaces dropped, then each character upper-cased on its own, as NTFS
 * compares names (one character to one; "ß" stays itself).
 */
export const windowsNameKey = (name) =>
  Array.from(String(name).replace(/[. ]+$/g, ""), (ch) => {
    const up = ch.toUpperCase();
    return Array.from(up).length === 1 ? up : ch;
  }).join("");

/** Slug a device name into a short folder suffix: "Canon R5" -> "canonr5" (backend deviceSlug). */
export const deviceSlug = (device) => {
  const s = (device ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  return s ? s.slice(0, 8) : null;
};

/**
 * The raw folder a new raw set gets (backend attemptRawFolderFor; the shared
 * vector holds both to it): the attempt's name; when taken, the device slug,
 * then the slug and a counter. Taken: between attempt-1 raw sets the exact
 * string (as before attempts existed); with a retake involved, the same place
 * on Windows. existing = the subject's raw sets ({folder, attempt}). Null when
 * the attempt cannot be named.
 */
export const attemptRawFolderFor = (jobId, code, number, suffix, device, attempt, attempts, existing) => {
  const name = (sfx) => attemptRawFolderName(jobId, code, number, sfx, attempt, attempts);
  const mine = attempt ?? 1;
  const isTaken = (f) => (existing ?? []).some((r) => {
    const other = String(r?.folder ?? "");
    return mine === 1 && (r?.attempt ?? 1) === 1 ? other === f : windowsNameKey(other) === windowsNameKey(f);
  });
  let used = suffix ?? null;
  let folder = name(used);
  if (folder === null) return null;
  if (!isTaken(folder)) return { folder, suffix: used };
  const base = deviceSlug(device) ?? "cam";
  used = base;
  folder = name(used);
  let n = 2;
  while (isTaken(folder)) {
    used = `${base}${n}`;
    folder = name(used);
    n += 1;
  }
  return { folder, suffix: used };
};

/** The raw sets / exports of the subject's latest attempt (one from before attempts existed is attempt 1's). */
export const ofLatestAttempt = (subject, list) => {
  const n = latestAttempt(subject);
  return (list ?? []).filter((x) => (x.attempt ?? 1) === n);
};

/**
 * Ingest raw's worklist: the subject's latest attempt is pre-project/-export
 * and still misses a raw folder for at least one of its tools (or has none).
 * A retake (attempt 2+) of a subject already in a project still waits for its own raw.
 */
export const isWaitingOnRaw = (s) => {
  const raw = ofLatestAttempt(s, s?.rawSets);
  return (latestAttempt(s) > 1 || !(s?.projectIds?.length)) && !ofLatestAttempt(s, s?.exports).length &&
    (!raw.length || (s?.capturedBy ?? []).some((c) => !raw.some((r) => r.code === c)));
};

const WINDOWS_RESERVED = new Set(["CON", "PRN", "AUX", "NUL",
  ...Array.from({ length: 9 }, (_, i) => `COM${i + 1}`), ...Array.from({ length: 9 }, (_, i) => `LPT${i + 1}`)]);

/**
 * The display-name folder on disk (§2.6; backend displayFolderName): the
 * display name made safe for Windows, spaces and dashes kept.
 */
export const displayFolderName = (displayName) => {
  // eslint-disable-next-line no-control-regex
  let s = String(displayName ?? "").normalize("NFC").replace(/[\u0000-\u001f<>:"/\\|?*]+/g, "-").trim();
  s = s.slice(0, 100).replace(/[. ]+$/g, "").trim();
  if (!s || /^\.+$/.test(s)) return "_";
  if (WINDOWS_RESERVED.has(s.split(".")[0].trim().toUpperCase())) s = `_${s}`;
  return s;
};

/** The studio's clients root (§2.6, decided): where exports are saved and read. */
export const CLIENTS_ROOT = "C:\\Clients";

/** Where an export's folder goes: C:\Clients\{job}\Exports\{display folder}\ (the folder itself is the machine name). */
export const exportsFolderPath = (jobStem, displayName) =>
  `${CLIENTS_ROOT}\\${jobStem}\\Exports\\${displayFolderName(displayName)}\\`;

/** Raw folder name uses the same rule as exportFilename. */
export const rawFolderName = (jobId, code, number, suffix) =>
  exportFilename(jobId, code, number, suffix);

const safePathPart = (value) =>
  String(value ?? "v1").trim().replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || "v1";

/** Current version keeps the clean name; superseded → versions/<label>/<filename>. */
export const exportVersionPath = (jobId, code, number, suffix, version, versionCount = 1) => {
  const filename = exportFilename(jobId, code, number, suffix);
  if (versionCount <= 1 || version?.current) return filename;
  return `versions/${safePathPart(version?.label)}/${filename}`;
};

/** exportVersionPath for one attempt's export (attempt 1: exactly exportVersionPath). Null as attemptExportFilename. */
export const attemptExportVersionPath = (jobId, code, number, suffix, attempt, attempts, version, versionCount = 1) => {
  const filename = attemptExportFilename(jobId, code, number, suffix, attempt, attempts);
  if (filename === null) return null;
  if (versionCount <= 1 || version?.current) return filename;
  return `versions/${safePathPart(version?.label)}/${filename}`;
};

/** Project save filename = jobId + lowest subject number; collision → _p<serial>. */
export const projectFileName = (jobId, project, subjects = [], allProjects = []) => {
  if (!project) return "";
  const lowestOf = (p) => {
    const nums = subjects
      .filter((s) => (s.projectIds ?? []).includes(p.id))
      .map((s) => s.number)
      .filter((n) => Number.isFinite(n));
    return nums.length ? Math.min(...nums) : (p.number ?? 0);
  };
  const low = lowestOf(project);
  const base = `${jobId}_${pad3(low)}`;
  const sharers = allProjects.filter((p) => lowestOf(p) === low);
  const ownerSerial = sharers.length ?
    Math.min(...sharers.map((p) => p.number ?? Infinity)) :
    (project.number ?? 0);
  return (project.number ?? 0) === ownerSerial ? base : `${base}_p${pad2(project.number)}`;
};

/** Non-tethered gear (output codes) for the add-subject tool toggles. */
export const NON_TETHERED = [
  { code: "pht", label: "Camera / drone photos", device: "Canon R5" },
  { code: "rtc", label: "Leica RTC360", device: "Leica RTC360" },
  { code: "blk", label: "Leica BLK360", device: "Leica BLK360" },
  { code: "xgd", label: "XGrids", device: "XGrids Portal" },
];

/** Look up a gear row by its output code (falls back to the first). */
export const gearForCode = (code) =>
  NON_TETHERED.find((g) => g.code === code) ?? NON_TETHERED[0];

/**
 * Tethered gear (output codes) — born live in the software, no raw stage
 * (raw = project). Leo captures on-device then imports straight into Artec
 * Studio, so for ingest it's the same as the Spiders. Used by Ingest project.
 */
export const TETHERED = [
  { code: "sps", label: "Artec Space Spider", software: "Artec Studio", device: "Artec Space Spider" },
  { code: "leo", label: "Artec Leo", software: "Artec Studio", device: "Artec Leo" },
  { code: "nmb", label: "NimbleTrack", software: "DefinSight → Artec Studio", device: "Scanology NimbleTrack" },
];

/** Software is derived from the gear code — never chosen by the operator. */
export const SOFTWARE_FOR_CODE = {
  sps: "Artec Studio",
  leo: "Artec Studio",
  nmb: "DefinSight → Artec Studio",
  pht: "RealityScan",
  rtc: "Cyclone",
  blk: "Cyclone",
  xgd: "LCC Studio",
};
